import React from 'react';
import { capitalize } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useModalContext } from '@magoosh/context/modal_context';
import { DefaultFilledButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { adminPaths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import LatexPreview from './latex_preview';
import RequestEditsModal from './request_edits_modal';
import { DraftPrompt, PromptRequest } from './types';

interface Props {
  draftPrompt: DraftPrompt;
  onChange?: (draftPrompt: DraftPrompt) => void;
  promptRequest: PromptRequest;
  readOnly?: boolean;
}

const DraftPromptPreview: React.FC<Props> = (props) => {
  const { setModal } = useModalContext();

  const accept = () => {
    const path = adminPaths.api.promptRequestDraftPromptAccept(
      props.promptRequest.id,
      props.draftPrompt.id
    );

    fetch(path, {
      method: 'POST',
    }).then((response) => {
      props.onChange && props.onChange(response.draftPrompt);
    });
  };

  const isEditable = () => {
    return !props.draftPrompt.promptId && !props.readOnly && props.promptRequest.status === 'validating';
  };

  const presentRequestEditsModal = () => {
    setModal({
      header: 'Request Edits',
      body: <RequestEditsModal
        draftPrompt={props.draftPrompt}
        onAccept={props.onChange}
        onClose={() => setModal(null)}
        promptRequest={props.promptRequest}
      />,
      size: 'lg'
    });
  };

  const reject = () => {
    const path = adminPaths.api.promptRequestDraftPromptReject(
      props.promptRequest.id,
      props.draftPrompt.id
    );

    fetch(path, {
      method: 'POST',
    }).then((response) => {
      props.onChange && props.onChange(response.draftPrompt);
    });
  };

  return (
    <div>
      <div className="d-flex flex-column gap-l well well-hollow">
        <div className="align-items-center d-flex gap-s">
          <div className="flex-1">
            {
              props.draftPrompt.promptId ? (
                <div>
                  <a href={adminPaths.promptEdit(props.draftPrompt.promptId)}>
                    <strong>Prompt</strong> <small>({props.draftPrompt.id})</small>
                  </a>
                </div>
              ) : (
                <div><strong>Prompt</strong> <small>({props.draftPrompt.id})</small></div>
              )
            }
          </div>

          {(() => {
            if (props.draftPrompt.status === 'accepted') {
              return <div className="label label-success" onClick={reject} style={{ cursor: 'pointer' }}>Accepted</div>
            } else if (props.draftPrompt.status === 'rejected') {
              return <div className="label label-danger" onClick={accept} style={{ cursor: 'pointer' }}>Rejected</div>
            } else if (props.draftPrompt.status === 'created') {
              return <div className="label label-info">Created</div>
            } else {
              return <div className="label bg-gray-light">{capitalize(props.draftPrompt.status)}</div>
            }
          })()}

          {
            props.draftPrompt.content.audit && (
              <div>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${props.draftPrompt.id}`}>
                      {props.draftPrompt.content.audit.failureReasons.join('\n\n')}
                    </Tooltip>
                  }
                >
                  <div className={`label label-${props.draftPrompt.content.audit.pass ? 'success' : 'danger'}`}>
                    {props.draftPrompt.content.audit.pass ? 'Pass' : 'Fail'}
                  </div>
                </OverlayTrigger>
              </div>
            )
          }

          {
            isEditable() && (
               <div>
                <DefaultFilledButton onClick={presentRequestEditsModal} size="sm">
                  Request Edits
                </DefaultFilledButton>
              </div>
            )
          }
        </div>

        {
          props.draftPrompt.content.context && (
            <div>
              <strong>Context</strong>
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(props.draftPrompt.content.context, null, 2)}
              </pre>
            </div>
          )
        }

        <div className="d-flex gap-m">
          <div className="d-flex flex-1 flex-column gap-s">
            <div className="well well-highlight">
              <LatexPreview text={props.draftPrompt.content.prompt || ""} />
            </div>
          </div>

          <div className="d-flex flex-1 flex-column gap-s">
            <div><strong>Answer Choices</strong></div>

            <div className="d-flex flex-column gap-s">
              {
                (props.draftPrompt.content.answerChoices || []).map((answerChoice, idx) => (
                  <div key={idx} className="align-items-center d-flex gap-s well well-hollow">
                    <div style={{ minWidth: 20 }}>
                      {answerChoice.correct && <FontAwesomeIcon className="text-primary" icon="check" />}
                    </div>
                    <LatexPreview text={answerChoice.value} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>

        {
          props.draftPrompt.content.textExplanation && (
            <div className="d-flex flex-1 flex-column gap-s">
              <div><strong>Text Explanation</strong></div>
              <LatexPreview text={props.draftPrompt.content.textExplanation}/>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default DraftPromptPreview;
